import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Step 1: Request reset code
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/request-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("forgot.failedSendCode"));
        setLoading(false);
        return;
      }
      setSuccess(t("forgot.codeSent"));
      setStep(2);
    } catch {
      setError(t("forgot.networkErrorRetry"));
    }
    setLoading(false);
  };

  // Step 2: Verify code
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("forgot.invalidCode"));
        setLoading(false);
        return;
      }
      setResetToken(data.resetToken);
      setSuccess(t("forgot.codeVerified"));
      setStep(3);
    } catch {
      setError(t("forgot.networkErrorRetry"));
    }
    setLoading(false);
  };

  // Step 3: Reset password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!password || !confirmPassword) {
      setError(t("forgot.enterConfirmPassword"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("forgot.passwordsNoMatch"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, newPassword: password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("forgot.failedReset"));
        setLoading(false);
        return;
      }
      setSuccess(t("forgot.resetSuccess"));
      setStep(4);
    } catch {
      setError(t("forgot.failedResetRetry"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
      <img
        src="/reviewcounter-logo.png"
        alt="ReviewCounter"
        className="w-52 max-w-xs mb-6 object-contain"
      />
      <form
        onSubmit={
          step === 1
            ? handleEmailSubmit
            : step === 2
            ? handleCodeSubmit
            : handlePasswordSubmit
        }
        className="bg-white p-6 rounded-xl shadow max-w-sm w-full space-y-4"
      >
        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold text-center text-teal-600">{t("forgot.title")}</h1>
            <p className="text-gray-600 text-sm text-center">
              {t("forgot.emailHint")}
            </p>
            <input
              className="w-full border rounded p-2"
              type="email"
              placeholder={t("forgot.email")}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </>
        )}
        {step === 2 && (
          <>
            <h1 className="text-2xl font-bold text-center text-teal-600">{t("forgot.enterCodeTitle")}</h1>
            <p className="text-gray-600 text-sm text-center">
              {t("forgot.codeHint")}
            </p>
            <input
              className="w-full border rounded p-2 tracking-widest text-center font-mono text-lg"
              type="text"
              placeholder={t("forgot.codePlaceholder")}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              maxLength={6}
              minLength={6}
              pattern="\d{6}"
            />
            <button
              type="button"
              className="text-teal-600 hover:underline text-xs mt-1"
              disabled={loading}
              onClick={handleEmailSubmit}
            >
              {t("forgot.resendCode")}
            </button>
          </>
        )}
        {step === 3 && (
          <>
            <h1 className="text-2xl font-bold text-center text-teal-600">{t("forgot.resetTitle")}</h1>
            <p className="text-gray-600 text-sm text-center">
              {t("forgot.resetHint")}
            </p>
            <input
              className="w-full border rounded p-2"
              type="password"
              placeholder={t("forgot.newPassword")}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <input
              className="w-full border rounded p-2"
              type="password"
              placeholder={t("forgot.confirmNewPassword")}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </>
        )}
        {error && <div className="text-red-600 text-sm text-center">{error}</div>}
        {success && <div className="text-green-600 text-sm text-center">{success}</div>}
        {step < 4 && (
          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-2 rounded font-semibold hover:bg-teal-700 transition"
            disabled={loading}
          >
            {loading
              ? step === 1
                ? t("forgot.sending")
                : step === 2
                ? t("forgot.verifying")
                : t("forgot.resetting")
              : step === 1
              ? t("forgot.sendCode")
              : step === 2
              ? t("forgot.verifyCode")
              : t("forgot.resetTitle")}
          </button>
        )}
        {step === 4 && (
          <div className="text-center mt-4">
            <Link
              to="/login"
              className="text-teal-600 hover:text-teal-800 text-sm underline"
            >
              {t("forgot.backToSignIn")}
            </Link>
          </div>
        )}
        {step < 4 && (
          <div className="text-center">
            <Link
              to="/login"
              className="text-teal-600 hover:text-teal-800 text-sm underline"
            >
              {t("forgot.backToSignIn")}
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

export default ForgotPassword; 